import type {
  DirectAudioContext,
  AudioEventHandlers,
  StrategyController,
} from './types';

/**
 * Initialize direct audio playback for single file content.
 * Returns a controller for managing the audio element.
 */
export function initDirectAudio(
  ctx: DirectAudioContext,
  sourceUrl: string,
  handlers: AudioEventHandlers
): StrategyController {
  const { audioRef } = ctx;

  if (!audioRef.current) {
    return {
      setPlaybackRate: () => {},
      cleanup: () => {},
    };
  }

  // Omit crossorigin so same-origin cookies authenticate the initial request,
  // while a redirect to a presigned R2 URL can play as an ordinary media load.
  // Setting `use-credentials` here would require credentialed CORS from R2.
  audioRef.current.removeAttribute('crossorigin');
  audioRef.current.src = sourceUrl;
  audioRef.current.load();

  // Just forward canplay - playbackRate is managed by the controller
  const wrappedOnCanPlay = () => {
    handlers.onCanPlay();
  };

  // Attach event listeners
  audioRef.current.addEventListener('canplay', wrappedOnCanPlay, {
    once: true,
  });
  audioRef.current.addEventListener('ended', handlers.onEnded);
  audioRef.current.addEventListener('waiting', handlers.onWaiting);
  audioRef.current.addEventListener('playing', handlers.onPlaying);
  audioRef.current.addEventListener('timeupdate', handlers.onTimeUpdate);

  return {
    setPlaybackRate: (rate: number) => {
      if (audioRef.current) {
        audioRef.current.playbackRate = rate;
      }
    },

    cleanup: () => {
      audioRef.current?.removeEventListener('canplay', wrappedOnCanPlay);
      audioRef.current?.removeEventListener('ended', handlers.onEnded);
      audioRef.current?.removeEventListener('waiting', handlers.onWaiting);
      audioRef.current?.removeEventListener('playing', handlers.onPlaying);
      audioRef.current?.removeEventListener(
        'timeupdate',
        handlers.onTimeUpdate
      );
    },
  };
}
